import { FC, useCallback, useEffect, useState } from 'react';
import { FieldValues, UseFieldArrayUpdate, useFormContext } from 'react-hook-form';
// mui componennts
import { Grid, Button } from '@mui/material';
/// compoenents
import SelectController from 'controllers/select-controller';
import OutlinedInputController from 'controllers/outlined-input-controller';
// types
import { IPredecessor } from '../../types';
// api
import { useAppSelector } from 'redux-store/hooks/useAppSelector';
import { manageGanttSelector } from 'redux-store/selector';
import { IPhaseItem, IPhaseLinkedItem, TScheduleItemOptions } from 'redux-store/types';
import { LagTypeEnum, useGetCompanyQuery } from 'redux-store/api/company';
// helpers
import { handleCalculateEndDate, utcDateToZoned, zonedDateToUtc } from 'utils/postDateHelper';
// icons
import DeleteIcon from 'assets/icons/buttons/delete-red.svg';
// styles
import { styles } from './styles';
import dayjs from 'dayjs';

type PredecessorsCardProps = {
  index: number;
  itemsListOptions: TScheduleItemOptions;
  field: IPredecessor;
  fields: IPredecessor[];
  isEdit?: boolean;
  itemOptions: TScheduleItemOptions;
  remove: (index?: number | number[]) => void;
  update: UseFieldArrayUpdate<FieldValues, 'predecessors'>;
  recalculateMaintainLagDaysFromStartDate: ({
    predecessors,
    startDate,
  }: {
    predecessors: IPredecessor[];
    startDate: dayjs.Dayjs | Date;
  }) => void;
  updateMaintainLagDays: ({
    value,
    currentPredecessor,
    predecessors,
  }: {
    currentPredecessor: IPredecessor;
    predecessors: IPredecessor[];
    value: number;
  }) => void;
  disabled: boolean;
  lagType: LagTypeEnum;
  itemsList: IPhaseItem<IPhaseLinkedItem>[];
  currentWorkDays: number;
};

const PredecessorsCard: FC<PredecessorsCardProps> = ({
  index,
  itemsListOptions,
  field,
  fields,
  isEdit,
  recalculateMaintainLagDaysFromStartDate,
  updateMaintainLagDays,
  lagType,
  itemsList,
  itemOptions,
  currentWorkDays,
  remove,
  update,
  disabled,
}) => {
  const [predecessorOptions, setPredecessorOptions] = useState<TScheduleItemOptions>();

  const { data: companySetup } = useGetCompanyQuery(undefined);
  const { changedFromDatePicker } = useAppSelector(manageGanttSelector);

  const form = useFormContext();
  const taskStartDate = form.getValues('startDate');
  const itemEndDate = form.getValues('endDate');

  useEffect(() => {
    const optionItem = itemsListOptions?.find(
      (item) => Number(item?.value) === Number(field?.predecessorId)
    );

    setPredecessorOptions([
      {
        label: optionItem?.label,
        value: field.predecessorId,
        disabled: optionItem?.disabled,
        disabledItemMessage: optionItem?.disabledItemMessage,
        endDate: optionItem?.endDate,
        startDate: optionItem?.startDate,
      },
      ...itemOptions,
    ]);
  }, [field, itemOptions, itemsListOptions]);

  useEffect(() => {
    const handleWheel = (e) => {
      e.preventDefault();
    };

    const numberInputs = document.querySelectorAll('input[type="number"]');

    numberInputs.forEach((input) => {
      input.addEventListener('wheel', handleWheel, { passive: false });
    });

    return () => {
      numberInputs.forEach((input) => {
        input.removeEventListener('wheel', handleWheel);
      });
    };
  }, []);

  const handlePredecessorsChange = useCallback(
    (event) => {
      const selected = predecessorOptions?.find(({ value }) => {
        return value === event?.target?.value;
      });

      if (!isEdit && !changedFromDatePicker) {
        if (fields?.length === index + 1) {
          if (!!selected) {
            let minimumLagDay = Number(!!field.lagDays ? field.lagDays : 0);
            const itemStartDate = new Date(selected?.endDate);

            itemStartDate.setDate(new Date(itemStartDate).getDate() + 2 + minimumLagDay);
            itemStartDate.setHours(0, 0, 0, 0);

            form.setValue('startDate', itemStartDate);
            form.setValue('endDate', itemStartDate);
            form.setValue('workDays', Number(!!field.lagDays ? field.lagDays : 0) + 1);
          }
        }
      }

      update(index, {
        ...field,
        startDate: selected.startDate,
        endDate: selected.endDate,
        predecessorId: event.target.value,
      });

      const latestFields = form.getValues('predecessors');
      const startDate = form.getValues('startDate');

      recalculateMaintainLagDaysFromStartDate({ predecessors: latestFields, startDate });
    },
    [predecessorOptions, index, field, isEdit, changedFromDatePicker]
  );

  const getMaxDate = (predeccorsList: IPredecessor[]) => {
    if (!predeccorsList.length || !isEdit) return null;

    const predecessorIds = predeccorsList.map((item) => item?.predecessorId);
    const predecessorItems = itemsList?.filter((item) => predecessorIds?.includes(item?.id));

    const firstItem = predeccorsList?.find(
      (item) => item?.predecessorId === predecessorItems?.[0]?.id
    );

    let firstLagDays = firstItem.lagDays;

    if (lagType === LagTypeEnum.MAINTAIN) {
      firstLagDays = firstItem.workDaysDifference;
    }

    const max = predeccorsList?.reduce(
      (latestPredecessor, current) => {
        const currentItem = itemsListOptions.find((item) => item?.value === current?.predecessorId);
        const zonedCurentDate = utcDateToZoned(currentItem?.endDate);
        const workingDays = companySetup?.workingDays;

        let currentLagDays = Number(current?.lagDays) + 1;

        if (lagType === LagTypeEnum.MAINTAIN) {
          currentLagDays = current.workDaysDifference;

          const maintainSourceStartDate = zonedDateToUtc(zonedCurentDate);
          const maintainTypeItemStartDate = handleCalculateEndDate(
            maintainSourceStartDate,
            Number(currentLagDays) + 2,
            workingDays
          );

          const maintainTypeItemEndDate = handleCalculateEndDate(
            zonedDateToUtc(maintainTypeItemStartDate),
            currentWorkDays,
            workingDays
          );

          if (maintainTypeItemStartDate > latestPredecessor?.newStartDate) {
            return {
              id: current?.predecessorId,
              newStartDate: maintainTypeItemStartDate,
              newEndDate: maintainTypeItemEndDate,
              lagDays: Number(currentLagDays),
            };
          }

          return latestPredecessor;
        }

        const minimumSourceStartDate = zonedDateToUtc(zonedCurentDate);
        const minimumTypeItemStartDate = handleCalculateEndDate(
          minimumSourceStartDate,
          currentLagDays,
          workingDays,
          1
        );

        let minimumTypeItemEndDate;

        if (!!minimumTypeItemStartDate) {
          minimumTypeItemEndDate = handleCalculateEndDate(
            zonedDateToUtc(minimumTypeItemStartDate),
            currentWorkDays,
            workingDays
          );
        }

        return minimumTypeItemStartDate > latestPredecessor?.newStartDate
          ? {
              id: current?.predecessorId,
              newStartDate: minimumTypeItemStartDate,
              newEndDate: minimumTypeItemEndDate,
              lagDays: Number(currentLagDays),
            }
          : latestPredecessor;
      },
      {
        id: predecessorItems?.[0].id,
        lagDays: Number(firstLagDays),
        newStartDate: taskStartDate,
        newEndDate: itemEndDate,
      }
    );

    return max;
  };

  const handleChangeLagDays = useCallback(
    (event) => {
      new Promise((resolve) => {
        if (lagType === LagTypeEnum.MAINTAIN) {
          updateMaintainLagDays({
            value: Number(event?.target?.value),
            currentPredecessor: field,
            predecessors: fields,
          });
          return;
        }

        if (Number(event?.target?.value) < 0) {
          update(index, { ...field, lagDays: 0 });
          event?.stopPropagation();
          event?.preventDefault();
          resolve('changed');
        }

        if (Number(event?.target?.value) >= 0) {
          if (lagType === LagTypeEnum.MINIMUM) {
            update(index, { ...field, lagDays: event?.target?.value });
          }

          // if (lagType === LagTypeEnum.MAINTAIN) {
          //   update(index, {
          //     ...field,
          //     workDaysDifference: event?.target?.value,
          //     lagDays: field.lagDays > event?.target?.value ? event?.target?.value : field.lagDays,
          //   });
          // }
          resolve('changed');
        }
      }).then(() => {
        const maxPredecessorsDate = getMaxDate(form.watch('predecessors'));

        // if (lagType === LagTypeEnum.MAINTAIN) {
        //   form.setValue('startDate', maxPredecessorsDate?.newStartDate);
        //   form.setValue('endDate', maxPredecessorsDate?.newEndDate);
        // }

        if (maxPredecessorsDate?.newStartDate > taskStartDate && lagType === LagTypeEnum.MINIMUM) {
          form.setValue('startDate', maxPredecessorsDate?.newStartDate);
          form.setValue('endDate', maxPredecessorsDate?.newEndDate);
        }
      });
    },
    [index, field]
  );

  const getLagDaysName = () => {
    if (lagType === LagTypeEnum.MAINTAIN) {
      return `predecessors.${index}.workDaysDifference`;
    }

    return `predecessors.${index}.lagDays`;
  };

  return (
    <Grid sx={styles.predecessorsCard}>
      <Grid sx={styles.predecessorsItemName}>
        <SelectController
          required
          withError
          isSelect
          fullWidth
          options={predecessorOptions}
          defaultValue={field.predecessorId || ''}
          name={`predecessors.${index}.predecessorId`}
          onChange={handlePredecessorsChange}
          placeholder="Select Item"
          disabled={disabled}
        />
      </Grid>

      <Grid sx={styles.predecessorsLagDays}>
        <OutlinedInputController
          required
          fullWidth
          withError
          type="number"
          placeholder="E.g. 2"
          name={getLagDaysName()}
          additionalOnChange={handleChangeLagDays}
          disabled={disabled}
        />
      </Grid>

      <Grid sx={styles.deleteButtonContainer}>
        {!disabled && (
          <Button sx={styles.deleteButton} onClick={() => remove(index)}>
            <DeleteIcon />
          </Button>
        )}
      </Grid>
    </Grid>
  );
};

export default PredecessorsCard;
